// kfe-l2b-test-exercise.js — Language-to-Body testing screen.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Exercise/L2B/L2BTestScreen.cs
//              (port equivalent of L2BExerciseScreen.cs but for testing)
//
// Consumes L2BTestEngine via state snapshots only. Tile UI mirrors training
// (see kfe-l2b-exercise.js) — same kfe-l2b-drag-area drag-and-drop gesture;
// the differences:
//   • No intro phase
//   • Fixed run length (numberOfInstances)
//   • Pass/fail summary at the end
//
// Pool resolution (Brief 006):
//   1. Pull MMUs the learner has met (any streak entry), filtered to L2B-eligible.
//   2. If fewer than `numberOfInstances`, top up from the full L2B registry.
//      The brief specifies the lesson's L2B pool as fallback, but the active
//      pathway tree has no l2b_test items today, so a global L2B registry
//      fallback is the closest practical equivalent.
//
// Persistence (Brief 006 step 6):
//   Mid-test progress is written to itemProgress.testState so a reload
//   resumes at the same round.
//
// Persistence ordering (Brief 199, criterion 1 — the choice and why):
//   This screen writes the SAME `itemProgress` row from two places: the
//   per-round `_persistProgress` (on every pick, the last one included — on the
//   final pick the engine mode is "feedback", not "complete", so that method's
//   own guard does not fire) and `_persistComplete`. Both used to be
//   fire-and-forget read-modify-writes across two transactions each, so the
//   last round's put — which had read the row BEFORE the completion wrote it —
//   landed afterwards and restored `completedCount: 0` with that round's
//   `testState` still on it. The 2022 full walk lost three completions exactly
//   that way: `item_105458` (stage 12), `item_110002` (18), `item_110665` (32).
//
//   The fix taken is MERGE-AT-WRITE-TIME, in `learner-store.updateItemProgress`
//   (and in every other `itemProgress` writer) rather than sequencing here:
//   `mutateRecord` does the read and the put inside ONE IndexedDB readwrite
//   transaction, so a patch that only means to change `testState` can no longer
//   carry a stale `completedCount` with it. Chosen over sequencing because the
//   hazard is not L2B-specific — `incrementPlayCount`, `applyPlaycountAdjustment`
//   and the Global-Playlist hook all write this row — and because transaction
//   isolation also holds across tabs, which an in-screen sequence would not.
//
//   What is sequenced here, in addition, is intent rather than safety:
//   `_persistProgress` is a no-op once the completion is persisted or in flight,
//   and `_persistComplete` awaits any in-flight round write, then nulls
//   `testState`, then records the completion — one after another, never raced.
//   Without that the row could keep a dead `testState` from the last round even
//   though the count is now safe.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { persistCompletion } from "./completion-persist.js";
import {
  L2BTestEngine,
} from "../engines/l2b-test-engine.js";
import {
  applyL2bDefensiveFilters,
  mmusToItemData,
} from "../engines/l2b-engine.js";
import { modeContentPredicate, l2bModeRenderFlags } from "../data/mmu-loader.js";

const FEEDBACK_MS = 900;
const DEFAULT_NUM_INSTANCES = 8;
const DEFAULT_PASS_SCORE = 70;

/** @param {string|null} l2bType → L2BTestConfig.modality */
function modalityFor(l2bType) {
  if (l2bType === "Text only") return "textOnly";
  if (l2bType === "Audio and Text") return "audioAndText";
  return "audioOnly";
}

export class KFEL2BTestExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; }
    h2 { margin-top: 0; }
    .target { margin: 1rem 0; padding: 0.75rem; border-radius: 8px;
              background: var(--kfe-surface); display: flex;
              align-items: center; gap: 1rem; }
    .target .word { font-size: var(--kfe-h2); flex: 1; }
    .grid { margin-top: 1rem; }
    .summary { padding: 2rem; text-align: center; }
    .summary .verdict { font-size: var(--kfe-h1); margin-bottom: 0.5rem; }
    .summary .verdict.pass { color: var(--kfe-success, #4caf50); }
    .summary .verdict.fail { color: var(--kfe-error, #e57373); }
    .summary .score { font-size: var(--kfe-h2); margin-bottom: 0.75rem; }
    .summary .threshold { color: var(--kfe-muted); margin-bottom: 1.25rem; }
    .empty { padding: 2rem; text-align: center; color: var(--kfe-muted); }
    button.primary { padding: 0.5rem 1rem; border-radius: 8px;
                     background: var(--kfe-accent); color: var(--kfe-on-accent);
                     border: 0; cursor: pointer; }
    button.replay { padding: 0.5rem 1rem; border-radius: 8px;
                    border: 1px solid var(--kfe-border); background: transparent;
                    color: var(--kfe-fg); cursor: pointer; }
    .start-prompt { padding: 2rem; text-align: center; }
    .progress { font-size: 0.85rem; color: var(--kfe-muted);
                text-align: right; margin-bottom: 0.25rem; }
    /* Feedback shown briefly (FEEDBACK_MS = 900ms) after a tap. Positioned
       prominently and large enough that it's caught even when the user's
       attention is on the tapped tile. */
    .feedback { padding: 1rem 1.5rem; border-radius: 12px; margin: 0.75rem 0;
                font-weight: 700; font-size: 2.5rem; text-align: center;
                line-height: 1; }
    .feedback.correct { background: #1f3d1f; color: #4caf50;
                        border: 2px solid #4caf50; }
    .feedback.wrong   { background: #3d1f1f; color: #e57373;
                        border: 2px solid #e57373; }
  `;

  /** @type {L2BTestEngine | null} */
  engine = null;

  constructor() {
    super();
    this._started = false;
    this._notEnoughMet = false;
    /** @type {string|null} */ this._lastAudioConceptId = null;
    /** @type {ReturnType<typeof setTimeout>|null} */ this._feedbackTimer = null;
    this._numberOfInstances = DEFAULT_NUM_INSTANCES;
    this._passScore = DEFAULT_PASS_SCORE;
    // Brief 101: per-mode stimulus rendering flags + mode-content pool filter.
    /** @type {string|null} */ this._l2bType = null;
    this._renderFlags = { showText: true, autoPlay: true };
    // Brief 072 one-shot guard: the engine could re-emit `completed` if a
    // resume() landed at the last round. The persisted completion increments
    // `completedCount` per Brief 072, so we must not double-count within one
    // screen lifecycle. _onRedo resets to false so a fresh run re-arms.
    this._completedPersisted = false;
    // Brief 199: the completion write is in flight (so a re-emitted `completed`
    // does not start a second one), and the last in-flight per-round write,
    // which `_persistComplete` awaits before it nulls `testState`.
    this._completedPending = false;
    /** @type {Promise<any>|null} */ this._progressWrite = null;
  }

  async onConnected() {
    await services.mmus.loadMmuRegistry();
    await services.items.loadContentItems();

    const itemId = Number(/** @type {any} */ (this).itemId);

    // Brief 101: the Testing item's L2B mode drives both the stimulus rendering
    // and the mode-content pool filter below.
    this._l2bType = services.mmus.getL2bType(itemId);
    this._renderFlags = l2bModeRenderFlags(this._l2bType);

    // Read per-item config (test items currently have no fields for these,
    // so defaults apply).
    const item = Number.isFinite(itemId) ? services.items.getItem(itemId) : undefined;
    /** @type {any} */ const anyItem = item;
    const cfgInstances = Number(anyItem?.numberOfInstances);
    const cfgPass      = Number(anyItem?.passScore);
    if (Number.isFinite(cfgInstances) && cfgInstances > 0) this._numberOfInstances = cfgInstances;
    if (Number.isFinite(cfgPass) && cfgPass >= 0)         this._passScore = cfgPass;

    // Build the test pool: met MMUs first, then top up from the global L2B
    // registry if too few. Preserve met-first ordering — the engine shuffles
    // anyway but it's a clearer audit trail.
    const metIds = new Set(await services.learner.getMetMmuIds());
    /** @type {import("../data/mmu-loader.js").Mmu[]} */
    let pool = [];
    // Mode-content filter (Brief 101 / iOS KFEContainerManager.m:102-143): an
    // Audio-only test must draw only MMUs with audio, a Text-only test only MMUs
    // with a phrase, Audio-and-Text both. Applied to the WHOLE L2B universe up
    // front so it gates BOTH the target pool (met ∩ filtered) AND the decoy
    // top-up below — the engine builds tiles from this single filtered pool, so
    // targets and distractors are both mode-compatible. A null/unknown mode
    // imposes no filter (pre-101 behaviour).
    const modeOk = modeContentPredicate(this._l2bType);
    const allL2b = applyL2bDefensiveFilters(services.mmus.getAllL2b()).filter(modeOk);
    for (const m of allL2b) if (metIds.has(m.mmuId)) pool.push(m);

    if (pool.length < this._numberOfInstances) {
      const seen = new Set(pool.map((m) => m.mmuId));
      for (const m of allL2b) {
        if (pool.length >= this._numberOfInstances) break;
        if (!seen.has(m.mmuId)) pool.push(m);
      }
    }

    if (pool.length < 4) {
      // Even after fallback we can't form a tile grid. Surface a friendly
      // empty state instead of throwing.
      this._notEnoughMet = true;
      this._scheduleRender();
      return;
    }

    const itemData = mmusToItemData(pool);

    this.engine = new L2BTestEngine(itemData, {
      numberOfInstances: this._numberOfInstances,
      passScore: this._passScore,
      modality: modalityFor(this._l2bType),
    });

    const onState = () => this._scheduleRender();
    const onResult = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      const mmuId = Number(detail.conceptId);
      if (Number.isFinite(mmuId)) {
        // Test attempts also feed mastery streaks (matches iOS behaviour:
        // test rounds count toward overall recall tracking).
        services.learner.recordMmuAttempt(mmuId, !!detail.correct, 3).catch((err) => {
          console.warn("[l2b-test] recordMmuAttempt failed", err);
        });
      }
    };
    const onComplete = () => this._persistComplete();

    this.engine.addEventListener("stateChanged", onState);
    this.engine.addEventListener("result", onResult);
    this.engine.addEventListener("completed", onComplete);
    this.addCleanup(() => this.engine?.removeEventListener("stateChanged", onState));
    this.addCleanup(() => this.engine?.removeEventListener("result", onResult));
    this.addCleanup(() => this.engine?.removeEventListener("completed", onComplete));

    // If a prior run was mid-flight, resume it. Distractors are re-rolled on
    // resume — same as iOS on view re-entry.
    const prog = await services.learner.getItemProgress(itemId);
    /** @type {any} */ const prior = prog?.testState;
    if (prior && typeof prior === "object" && Number.isFinite(prior.roundIndex) && prior.roundIndex > 0 && prior.roundIndex < this._numberOfInstances) {
      this._started = true;
      const state = this.engine.resume(prior);
      this._afterStateChange(state);
    }

    this._scheduleRender();
  }

  onDisconnected() {
    if (this._feedbackTimer) clearTimeout(this._feedbackTimer);
    services.audio.stop();
    // Persist current progress so a reload resumes at the same round.
    this._persistProgress();
  }

  _handleStart() {
    if (!this.engine || this._started) return;
    this._started = true;
    const state = this.engine.start();
    this._afterStateChange(state);
  }

  _handleReplay() {
    if (!this.engine) return;
    const state = this.engine.snapshot();
    const target = state.targetItem;
    if (target) this._playAudioForTarget(target, /*force*/ true);
  }

  /** @param {string} conceptId */
  _handlePick(conceptId) {
    if (!this.engine) return;
    if (this._feedbackTimer) return;
    const fb = this.engine.onTileSelected(conceptId);
    this._afterStateChange(fb);
    this._persistProgress();
    this._feedbackTimer = setTimeout(() => {
      this._feedbackTimer = null;
      const next = this.engine?.onFeedbackComplete();
      if (next) this._afterStateChange(next);
    }, FEEDBACK_MS);
  }

  _handleRestart() {
    // Drop persisted state and re-run onConnected logic.
    const itemId = Number(/** @type {any} */ (this).itemId);
    services.learner.updateItemProgress(itemId, { testState: null }).catch(() => {});
    this._started = false;
    this._notEnoughMet = false;
    this.engine = null;
    this.onConnected();
  }

  // Brief 046 completion-screen handlers. _onRedo nukes the persisted test
  // state and rebuilds; _onReturnToLesson walks back to the lesson detail.
  // Mirrors the same wiring used by L2B Training.
  async _onRedo() {
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (Number.isFinite(itemId)) {
      try {
        await services.learner.clearItemProgress(itemId, []);
      } catch (err) {
        console.warn("[l2b-test] clearItemProgress failed", err);
      }
    }
    if (this._feedbackTimer) { clearTimeout(this._feedbackTimer); this._feedbackTimer = null; }
    services.audio.stop();
    this._started = false;
    this._notEnoughMet = false;
    this._lastAudioConceptId = null;
    this._completedPersisted = false;
    this._completedPending = false;
    this._progressWrite = null;
    this.engine = null;
    this._scheduleRender();
    this.onConnected();
  }

  _onReturnToLesson() {
    history.back();
  }

  /** @param {import("../engines/l2b-test-engine.js").L2BTestState} state */
  _afterStateChange(state) {
    // Brief 101: Text-only mode suppresses auto-play (stimulus is the visible
    // phrase); the replay button still plays on demand.
    if (state.mode === "tileGrid" && state.targetItem && this._renderFlags.autoPlay) {
      this._playAudioForTarget(state.targetItem, false);
    }
  }

  /**
   * @param {import("../engines/l2b-engine.js").L2BItemData} target
   * @param {boolean} force
   */
  _playAudioForTarget(target, force) {
    if (!force && this._lastAudioConceptId === target.conceptId) return;
    this._lastAudioConceptId = target.conceptId;
    services.audio.play(target.mmu).catch((e) => {
      console.warn("[l2b-test] audio play failed for", target.conceptId, e);
    });
  }

  _persistProgress() {
    if (!this.engine) return;
    // Brief 199: once the completion is recorded (or on its way), the test's
    // mid-flight state is dead data — writing it back would leave a `testState`
    // on a completed row. The count itself is safe either way now that
    // `updateItemProgress` merges at write time.
    if (this._completedPersisted || this._completedPending) return;
    const s = this.engine.snapshot();
    if (s.mode === "complete") return;
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(itemId)) return;
    // resume() re-presents `_testList[roundIndex]`, so the persisted index
    // must point to the NEXT round to play — i.e. the count of rounds that
    // already have a result.
    const nextRound = s.roundResults.length;
    // Kept as the last in-flight round write so `_persistComplete` can await it
    // instead of racing it.
    this._progressWrite = services.learner.updateItemProgress(itemId, {
      testState: {
        roundIndex: nextRound,
        correctCount: s.correctCount,
        errorCount: s.errorCount,
        results: s.roundResults,
      },
    }).catch((err) => console.warn("[l2b-test] persist progress failed", err));
    return this._progressWrite;
  }

  async _persistComplete() {
    if (!this.engine) return;
    const s = this.engine.snapshot();
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(itemId)) return;
    // Brief 072: migrate from set-to-1 to markItemCompleted increment so the
    // L2B Testing finish path matches every other terminal-completion writer.
    // `completedCount` is a play counter (iOS canon — KFEDatabaseManager.m:494
    // `set completed = ++numberOfCompletions`), not a flag, so re-passing this
    // item bumps the count instead of clobbering it to 1.
    // Brief 199, criterion 3: the latch goes up only after the completion write
    // has actually landed; `_completedPending` holds a re-emitted `completed`
    // (a resume() that lands on the last round can fire one) while it is in
    // flight, so one completion is still exactly one increment.
    if (this._completedPersisted || this._completedPending) return;
    this._completedPending = true;
    // Brief 199, criterion 1: the three writes of a finished test run in order —
    // the last round's progress write, then clearing `testState`, then the
    // completion — never two at once on the same row.
    try {
      if (this._progressWrite) await this._progressWrite;
      await services.learner.updateItemProgress(itemId, { testState: null });
    } catch (err) {
      console.warn("[l2b-test] clear testState failed", err);
    }
    const ok = await persistCompletion(itemId);
    this._completedPending = false;
    this._completedPersisted = ok;

    // Brief 006 §Gotchas: emit a `test_attempt` session record. The sync
    // engine (Brief 025) will pick this up later; for now it's local-only.
    services.learner.recordSession({
      startedAt: Date.now() - (s.totalRounds * 5000), // rough estimate
      endedAt: Date.now(),
      exerciseType: "l2b_test",
      itemId,
      summary: {
        totalRounds: s.totalRounds,
        correctCount: s.correctCount,
        errorCount: s.errorCount,
        accuracyPercent: s.accuracyPercent,
        passScore: s.passScore,
        passed: s.passed,
      },
    }).catch((err) => console.warn("[l2b-test] recordSession failed", err));
  }

  render() {
    if (this._notEnoughMet) {
      return html`<div class="empty">
        <h2>${t("l2bTest.title")}</h2>
        <p>${t("l2bTest.notEnoughMet")}</p>
      </div>`;
    }
    if (!this.engine) return html`<p>${t("loading")}</p>`;

    if (!this._started) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "primary";
      btn.textContent = t("lesson.start");
      btn.addEventListener("click", () => this._handleStart());
      return html`
        <h2>${t("l2bTest.title")}</h2>
        <div class="start-prompt">${btn}</div>
      `;
    }

    const s = this.engine.snapshot();

    if (s.mode === "complete") {
      const verdict = s.passed ? "pass" : "fail";
      const verdictText = s.passed ? t("l2bTest.passed") : t("l2bTest.failed");
      const percent = Math.round(s.accuracyPercent);

      // Brief 046: shared completion screen. The score block is passed as a
      // child of the slot so it renders above the action row.
      const summary = document.createElement("div");
      summary.className = "summary";
      summary.innerHTML = `
        <div class="verdict ${verdict}">${verdictText}</div>
        <div class="score"></div>
        <div class="threshold"></div>
      `;
      const score = summary.querySelector(".score");
      if (score) score.textContent = t("l2bTest.score", {
        correct: String(s.correctCount),
        total: String(s.totalRounds),
        percent: String(percent),
      });
      const thr = summary.querySelector(".threshold");
      if (thr) thr.textContent = t("l2bTest.threshold", {
        percent: String(Math.round(s.passScore)),
      });

      // Brief 049: iOS-canonical L2B Testing summary (KFELTBSummaryView) shows
      // the verdict/score, then the prompt "Do the Test Again?" (再测试一遍吗？)
      // with "Yes"/"No" (好/不) stacked beneath. NB: the iOS IBAction selectors
      // are named goOnTesting:/next: — those are NOT the on-screen labels (an
      // earlier brief draft mistook the selector names for button text). Yes
      // redoes the test; No returns to the lesson.
      const screen = document.createElement("kfe-completion-screen");
      /** @type {any} */ (screen).message = t("l2bTest.title");
      /** @type {any} */ (screen).prompt = t("completion.l2bTesting.prompt");
      /** @type {any} */ (screen).layout = "column";
      /** @type {any} */ (screen).actions = [
        { label: t("completion.l2bTesting.yes"), eventName: "kfe:l2bTesting:yes", primary: true },
        { label: t("completion.l2bTesting.no"), eventName: "kfe:l2bTesting:no" },
      ];
      screen.appendChild(summary);
      screen.addEventListener("kfe:l2bTesting:yes", () => this._onRedo());
      screen.addEventListener("kfe:l2bTesting:no", () => this._onReturnToLesson());
      return html`${screen}`;
    }

    const target = s.targetItem;
    if (!target) return html`<p>${t("loading")}</p>`;

    // L2B Testing uses drag-and-drop, identical gesture to Training (iOS
    // L2BTestingContainerViewController reuses KFEContentsView/KFETargetsView;
    // the Unity port reuses L2BDraggableTile + OnTileDropped). Training and
    // Testing differ only in feedback timing and scoring, after the drop.
    // `disabled` suppresses a second drop during the feedback window — same
    // as the screen's _handlePick `if (this._feedbackTimer) return;` guard.
    const dragArea = document.createElement("kfe-l2b-drag-area");
    /** @type {any} */ (dragArea).items = s.tiles.map((d) => d.mmu);
    /** @type {any} */ (dragArea).disabled = s.mode === "feedback";
    dragArea.addEventListener("pick", (e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      this._handlePick(String(detail.mmuId));
    });

    const replay = document.createElement("button");
    replay.type = "button";
    replay.className = "replay";
    replay.textContent = t("audio.replay");
    replay.addEventListener("click", () => this._handleReplay());

    const fbBlock = s.mode === "feedback"
      ? html`<div class="feedback ${s.wasCorrect ? "correct" : "wrong"}">
          ${s.wasCorrect ? "✓" : "✗"}
        </div>`
      : html``;

    const progressText = t("l2bTest.progress", {
      current: String(Math.min(s.currentRound + 1, s.totalRounds)),
      total: String(s.totalRounds),
    });

    return html`
      <div class="progress">${progressText}</div>
      <h2>${t("l2bTest.title")}</h2>
      <div class="target">
        ${this._renderFlags.showText
          ? html`<span class="word">${target.displayText}</span>`
          : html`<span class="word" aria-hidden="true"></span>`}
        ${replay}
      </div>
      ${fbBlock}
      <div class="grid">${dragArea}</div>
    `;
  }
}
